import { Alert, Snackbar } from "@mui/material";
import React, { useState, useEffect, useRef } from "react";

// Utility function to convert a buffer to base64
function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

// Encrypt AES-CBC, IV+base64
export async function encryptData(plaintext, key) {
  const iv = window.crypto.getRandomValues(new Uint8Array(16));
  const keyBytes = new TextEncoder().encode(key);
  const plaintextBytes = new TextEncoder().encode(plaintext);

  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-CBC" },
    false,
    ["encrypt"]
  );
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-CBC", iv },
    cryptoKey,
    plaintextBytes
  );
  const encryptedBytes = new Uint8Array(encryptedBuffer);
  const combined = new Uint8Array(iv.length + encryptedBytes.length);
  combined.set(iv);
  combined.set(encryptedBytes, iv.length);
  return arrayBufferToBase64(combined);
}

// Decrypts base64(IV+ciphertext)
export async function decryptData(ciphertext, key) {
  try {
    const data = Uint8Array.from(atob(ciphertext), c => c.charCodeAt(0));
    if (data.length < 17) throw new Error("Ciphertext too short");
    const iv = data.slice(0, 16);
    const encryptedBytes = data.slice(16);
    const keyBytes = new TextEncoder().encode(key);

    const cryptoKey = await window.crypto.subtle.importKey(
      "raw",
      keyBytes,
      { name: "AES-CBC" },
      false,
      ["decrypt"]
    );
    const buffer = await window.crypto.subtle.decrypt(
      { name: "AES-CBC", iv },
      cryptoKey,
      encryptedBytes
    );
    return new TextDecoder().decode(buffer);
  } catch (err) {
    throw new Error("Decryption failed: " + err.message);
  }
}

export function CryptoTool({ defaultKey = 'A1b2C3d4E5f6G7h8I9j0K1l2M3n4O512', value = '', readOnly = false,Encrypt }) {
  const [input, setInput] = useState(value || "");
  const [key, setKey] = useState(defaultKey);
  const [isEncrypt, setIsEncrypt] = useState(Encrypt);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const resultRef = useRef();
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // update input if parent sends us a new value
  useEffect(() => { setInput(value || "") 
    process()}, [value]);

  const clearFields = () => { setInput(""); setResult(""); };

  const copyResult = () => {
    if (resultRef.current) {
      resultRef.current.select();
      document.execCommand("copy");
      // window.alert("Copied!");
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenSnackbar(false);
  };
  const process = async () => {
    setLoading(true);
    setResult("");
    try {
      if (!input || !key) {
        setResult("Input and key required.");
        return;
      }
      if (isEncrypt) {
        const encrypted = await encryptData(input, key);
        setResult(encrypted);
      } else {
        let decrypted = await decryptData(input, key);
        // try to pretty-print JSON output
        try { decrypted = JSON.stringify(JSON.parse(decrypted), null, 2); } catch {}
        setResult(decrypted);
      }
    } catch (err) {
      setResult("" + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
      <div style={{
      border: "1px solid ",
      borderRadius: 8,
      padding: 24,
      // maxWidth: 600,
      
      margin: "2rem auto",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      backgroundColor: "#fff",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
      color: "#222",
      userSelect: "none"
    }}>
      
      <div>
        <b>{isEncrypt ? "Encrypted (Base64):" : "Decrypted text:"}</b>
        <textarea
          ref={resultRef}
          value={result}
          rows={5}
          readOnly
         style={{
          width: "100%",
          resize: "vertical",
          padding: 12,
          fontSize: 14,
          borderRadius: 6,
          border: "1.5px solid #ccc",
          backgroundColor: "#f9f9f9",
          color: loading ? "#999" : "#111",
          fontFamily: "'Courier New', Courier, monospace",
          boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)"
        }}
        />
        <button onClick={copyResult} style={{ marginTop: 6 }}>
          Copy Result
        </button>
      </div>
       <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Copied!
        </Alert>
      </Snackbar>
    </div>
  );
}
